// ---------------------------------------------------------------------------
// Dashboard — role-scoped summary landing page
// ---------------------------------------------------------------------------
// Answers "what needs my attention right now?" at a glance. The full template
// management table with search/filter/pagination lives in TemplateList.tsx
// (the /templates route).

import { useNavigate, Link } from 'react-router';
import {
  Plus,
  Pencil,
  Eye,
  Inbox,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTemplates } from '@/hooks/use-templates';
import { useApprovalQueue } from '@/hooks/use-workflow';
import { REVIEW_QUEUE_STATUSES, canCreateTemplate } from '@/types/workflow';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, statusLabel } from '@/lib/utils';
import type { Template, TemplateStatus } from '@/types/template';

// ---------------------------------------------------------------------------
// Status card config
// ---------------------------------------------------------------------------

interface StatusCard {
  status: TemplateStatus;
  label: string;
  icon: typeof FileText;
  color: string; // tailwind bg + text classes
}

const STATUS_CARDS: StatusCard[] = [
  {
    status: 'draft',
    label: 'Drafts',
    icon: Pencil,
    color: 'bg-silver-100 text-carbon-600',
  },
  {
    status: 'pending_internal_review',
    label: 'Pending Review',
    icon: Clock,
    color: 'bg-purple-100 text-purple-700',
  },
  {
    status: 'live',
    label: 'Live',
    icon: CheckCircle2,
    color: 'bg-green-100 text-green-700',
  },
  {
    status: 'revision_requested',
    label: 'Revisions Requested',
    icon: AlertCircle,
    color: 'bg-red-100 text-red-700',
  },
];

const STATUS_BADGE_MAP: Record<TemplateStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  draft: 'secondary',
  pending_internal_review: 'default',
  tcm_admin_approved: 'default',
  pending_client_review: 'warning',
  live: 'success',
  revision_requested: 'destructive',
};

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const hasQueue = (REVIEW_QUEUE_STATUSES[user.role]?.length ?? 0) > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-carbon-900">
            Welcome back, {user.email.split('@')[0]}
          </h2>
          <p className="text-sm text-carbon-500">
            {user.role} &middot; Here&apos;s what needs your attention
          </p>
        </div>
        {canCreateTemplate(user.role) && (
          <Button onClick={() => navigate('/templates/new')}>
            <Plus className="h-4 w-4 mr-1" />
            New Template
          </Button>
        )}
      </div>

      {/* Status counts */}
      <StatusCountsRow />

      {/* Review queue — only for roles that have one */}
      {hasQueue && <ReviewQueueSection />}

      {/* Recently updated templates */}
      <RecentTemplatesSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status counts row — clickable cards that navigate to filtered Templates view
// ---------------------------------------------------------------------------

function StatusCountsRow() {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {STATUS_CARDS.map((card) => (
        <StatusCountCard key={card.status} card={card} />
      ))}
    </div>
  );
}

function StatusCountCard({ card }: { card: StatusCard }) {
  const { data, isLoading } = useTemplates({
    status: card.status,
    page: 1,
    pageSize: 1,
  });

  const count = data?.total ?? 0;

  return (
    <Link
      to={`/templates?status=${card.status}`}
      className="group flex items-center gap-4 rounded-ds-008 border border-silver-200 bg-white px-5 py-4 transition-colors hover:border-purple-300 hover:bg-purple-50/30"
    >
      <div className={`flex h-10 w-10 items-center justify-center rounded-ds-008 ${card.color}`}>
        <card.icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-2xl font-bold text-carbon-900">
          {isLoading ? <Skeleton className="h-7 w-8 inline-block" /> : count}
        </p>
        <p className="text-xs text-carbon-500 truncate">{card.label}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-silver-300 group-hover:text-purple-400 transition-colors" />
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Review queue — expanded inline cards (not just a count)
// ---------------------------------------------------------------------------

function ReviewQueueSection() {
  const { user } = useAuth();
  const { data, isLoading } = useApprovalQueue(user?.role);
  const navigate = useNavigate();

  const templates = data?.templates ?? [];

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-purple-500" />
          <h3 className="text-base font-semibold text-carbon-900">
            Awaiting Your Review
          </h3>
          {!isLoading && templates.length > 0 && (
            <Badge variant="default" className="ml-1">
              {templates.length}
            </Badge>
          )}
        </div>
        <Link
          to="/approvals"
          className="text-xs font-medium text-purple-600 hover:text-purple-700"
        >
          View all &rarr;
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-ds-008 border border-silver-200 bg-white px-5 py-8 text-center">
          <Inbox className="mx-auto h-8 w-8 text-silver-300 mb-2" />
          <p className="text-sm text-carbon-500">
            No templates need your review right now
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.slice(0, 5).map((t) => (
            <QueueCard key={t.template_id} template={t} navigate={navigate} />
          ))}
          {templates.length > 5 && (
            <Link
              to="/approvals"
              className="block text-center text-xs text-purple-600 hover:text-purple-700 py-2"
            >
              +{templates.length - 5} more &mdash; view all
            </Link>
          )}
        </div>
      )}
    </section>
  );
}

function QueueCard({
  template: t,
  navigate,
}: {
  template: Template;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-ds-008 border border-silver-200 bg-white px-5 py-3 cursor-pointer transition-colors hover:border-purple-300 hover:bg-purple-50/30"
      onClick={() => navigate(`/templates/${t.template_id}/review`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(`/templates/${t.template_id}/review`);
      }}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-carbon-900 truncate">
          {t.title}
        </p>
        <p className="text-xs text-carbon-500 mt-0.5">
          {t.disease_association ?? 'No disease'} &middot; Updated{' '}
          {formatDate(t.modified_on ?? t.created_on)}
        </p>
      </div>
      <Badge variant={STATUS_BADGE_MAP[t.status]}>
        {statusLabel(t.status)}
      </Badge>
      <Eye className="h-4 w-4 text-purple-400 shrink-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recently updated templates — last 5 by update date
// ---------------------------------------------------------------------------

function RecentTemplatesSection() {
  const navigate = useNavigate();

  // Fetch the 5 most recently updated templates (API sorts by modified_on desc
  // by default when no keyword filter is active).
  const { data, isLoading } = useTemplates({ page: 1, pageSize: 5 });

  const templates = data?.entries ?? [];

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-carbon-400" />
          <h3 className="text-base font-semibold text-carbon-900">
            Recently Updated
          </h3>
        </div>
        <Link
          to="/templates"
          className="text-xs font-medium text-purple-600 hover:text-purple-700"
        >
          View all templates &rarr;
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-ds-008 border border-silver-200 bg-white px-5 py-8 text-center">
          <FileText className="mx-auto h-8 w-8 text-silver-300 mb-2" />
          <p className="text-sm text-carbon-500">No templates yet</p>
        </div>
      ) : (
        <div className="rounded-ds-008 border border-silver-200 bg-white divide-y divide-silver-100">
          {templates.map((t) => (
            <div
              key={t.template_id}
              className="flex items-center justify-between gap-4 px-5 py-3 cursor-pointer transition-colors hover:bg-silver-50"
              onClick={() => {
                const path = t.status === 'draft'
                  ? `/templates/${t.template_id}`
                  : `/templates/${t.template_id}/review`;
                navigate(path);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const path = t.status === 'draft'
                    ? `/templates/${t.template_id}`
                    : `/templates/${t.template_id}/review`;
                  navigate(path);
                }
              }}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-carbon-900 truncate">
                  {t.title}
                </p>
                <p className="text-xs text-carbon-500 mt-0.5">
                  {t.disease_association ?? 'No disease'} &middot;{' '}
                  {formatDate(t.modified_on ?? t.created_on)}
                </p>
              </div>
              <Badge variant={STATUS_BADGE_MAP[t.status]}>
                {statusLabel(t.status)}
              </Badge>
              {t.status === 'draft' ? (
                <Pencil className="h-4 w-4 text-carbon-400 shrink-0" />
              ) : (
                <Eye className="h-4 w-4 text-carbon-400 shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
